import React from 'react';
import { cn } from '../../lib/utils';

/**
 * ActionCard - A simple, non-gamified card for the 3 primary identity actions.
 * 
 * Design principles:
 * - Simple, clear, not shiny
 * - Feels like a natural action (writing, thinking, storing)
 * - If user feels "setting up AI" → design failed
 * - If user feels "talking to themselves" → design succeeded
 */
export function ActionCard({
  title,
  description,
  icon: Icon,
  onClick,
  variant = 'default',
  className,
  'data-testid': testId,
  disabled = false
}) {
  const variants = {
    default: 'bg-white border-gray-200 hover:border-gray-300',
    warm: 'bg-gradient-to-br from-[hsl(20,100%,97%)] to-white border-[hsl(25,95%,53%)]/20 hover:border-[hsl(25,95%,53%)]/40',
    sage: 'bg-white border-[hsl(145,25%,55%)]/30 hover:border-[hsl(145,25%,55%)]',
    muted: 'bg-gray-50 border-gray-200 hover:border-gray-300'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
        'flex flex-col justify-between h-56 md:h-64 p-6 md:p-8 rounded-2xl',
        'transition-all duration-300 ease-out',
        'hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(0,0,0,0.06)]',
        'active:scale-[0.98]',
        'border text-right',
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[hsl(25,95%,53%)]',
        'cursor-pointer',
        disabled && 'opacity-50 cursor-not-allowed hover:translate-y-0 hover:shadow-none',
        variants[variant],
        className
      )}
    >
      <div className="flex flex-col items-end gap-4">
        {Icon && (
          <div className="p-3 rounded-xl bg-gray-100/80">
            <Icon className="w-6 h-6 text-gray-700" strokeWidth={1.5} />
          </div>
        )}
        <h3 className="text-xl md:text-2xl font-semibold text-gray-900 text-balance">
          {title}
        </h3>
      </div>
      
      <p className="text-sm md:text-base text-gray-600 leading-relaxed">
        {description}
      </p>
    </button>
  );
}

export default ActionCard;
